/**
 * Axios 配置
 */

import axios from 'axios';
import i18n from '../i18n';
import { triggerSuspend } from './suspendSignal';

// 開發環境使用 localhost,生產環境使用相對路徑(由 nginx 代理)
const API_BASE_URL = process.env.REACT_APP_API_URL === undefined
  ? 'http://localhost:10181'  // 開發環境 (沒有設定 .env)
  : process.env.REACT_APP_API_URL === ''
  ? ''  // 生產環境 - 使用相對路徑
  : process.env.REACT_APP_API_URL;  // 自訂 URL

// 建立 axios 實例
const axiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// 公開端點（不需 auth）· 不塞 token，避免殘留過期 token 干擾（Bug #4 根因之一）
const PUBLIC_ENDPOINTS = [
  '/api/auth/policy',
  '/api/auth/login/start',
  '/api/auth/login/factor/',   // credential / captcha / totp / totp/enroll
  '/api/auth/captcha/',
  '/api/system/message-codes',
  '/api/system/languages',
  '/api/system/license',
  '/api/system/tenant-brand',
  '/api/system/profile',
  '/api/ee/auth/line/login',   // Phase C1：以 LINE 登入 /login + /login-complete（未登入即用）
];

// 請求攔截器 - 自動加入 Token + 當前 UI 語系
axiosInstance.interceptors.request.use(
  (config) => {
    const url = config.url || '';
    const isPublic = PUBLIC_ENDPOINTS.some((p) => url.includes(p));
    // 添加 Bearer Token (JWT / session_id)：public endpoint 不塞
    const token = localStorage.getItem('session_id') || localStorage.getItem('access_token');
    if (token && !isPublic) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // 帶當前 UI 語系，後端 AI prompt 用對應語言輸出
    if (i18n?.language) {
      config.headers['X-User-Lang'] = i18n.language;
    }

    // Transaction Token（SOP §16.3 — 寫入操作必要）
    const txnToken = localStorage.getItem('txn_token') || token;
    if (txnToken) {
      config.headers['X-Txn-Token'] = txnToken;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// 回應攔截器 - 處理錯誤
axiosInstance.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    if (error.response) {
      // responseType:'blob' 的端點（my_drive 下載/預覽、drive_share 預覽、ops_center 圖片、
      // mail 附件、未來 WOPI GetFile）錯誤時，error body 也被強制成 Blob → detail 解不到 →
      // showApiError fallback 錯碼（porsche 遇到的 ERR020004）。這裡先把 application/json 的 Blob
      // 還原成物件，讓下游（401/403 導頁 + showApiError）拿到真正的 detail。
      const errBody: unknown = error.response.data;
      if (errBody instanceof Blob && errBody.type === 'application/json') {
        try {
          error.response.data = JSON.parse(await errBody.text());
        } catch { /* silent: 還原失敗就維持原樣，走既有 fallback */ }
      }
      // 503 服務暫停（tenant service gate）：content negotiation JSON body 的 layer 優先，
      // 否則 X-Service-Suspended header（非一般 5xx；一般 server error 無這兩者，不誤判）
      if (error.response.status === 503) {
        const data: any = error.response.data;
        const scope = (data && typeof data === 'object' && (data.tenant_suspend === true) && data.layer)
          || error.response.headers?.['x-service-suspended'];
        if (scope === 'system' || scope === 'tenant') {
          triggerSuspend(scope);   // → SystemContext setIsService(false) 顯 MaintenancePage（friendly）
        }
      }
      // 401 未授權 - token 或 session 過期，導向登入頁
      // 但登入端點本身的 401（帳號密碼錯誤）不在此處理，由 LoginPage 自行處理
      if (error.response.status === 401) {
        const requestUrl = error.config?.url || '';
        // 這些 auth 端點的 401 由 caller 自行處理，不在此攔截導頁：
        //  - /api/auth/login*  → LoginPage 顯友善訊息
        //  - /api/auth/me / verify-session → AuthContext/authService silent（Bug #4：
        //    未 login user 進 login page 帶殘留過期 token，call /me 拿 401 是 normal case）
        const isAuthSelfHandled = requestUrl.includes('/api/auth/login')
          || requestUrl.includes('/api/auth/me')
          || requestUrl.includes('/api/auth/verify-session');
        if (!isAuthSelfHandled) {
          localStorage.removeItem('session_id');
          localStorage.removeItem('access_token');
          // 把後端回傳的訊息代碼（含參數）帶到登入頁，由 LoginPage 顯示
          // detail 可能是字串（純代碼）或物件（{code, params}）
          const detail = error.response.data?.detail;
          let msgParam = '';
          if (typeof detail === 'string') {
            msgParam = `?msg=${encodeURIComponent(detail)}`;
          } else if (detail && typeof detail === 'object' && detail.code) {
            const params = detail.params ? encodeURIComponent(JSON.stringify(detail.params)) : '';
            msgParam = `?msg=${encodeURIComponent(detail.code)}${params ? `&p=${params}` : ''}`;
          }
          window.location.href = `/login${msgParam}`;
        }
      }
      // 403 禁止存取
      else if (error.response.status === 403) {
        console.error('禁止存取:', error.response.data?.detail);
      }
    }
    return Promise.reject(error);
  }
);

export default axiosInstance;
